import { useState } from 'react';
import {
  CheckCircle2, Package, Truck, RefreshCw, FileText, XCircle, ArrowRightLeft, MapPin, Tag,
  ExternalLink, Loader2, ChevronDown, ImageOff, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '@/services/api';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';
import { ORDER_STATUS, STEPS, IN_TRANSIT_SET, money } from './orderStatus';
import { BookShipmentDialog } from './BookShipmentDialog';

const StatusBadge = ({ status }) => {
  const s = ORDER_STATUS[status] || { label: status, tone: 'bg-gray-100 text-gray-700' };
  return <Badge className={`${s.tone} border-0`} data-testid={`status-${status}`}>{s.label}</Badge>;
};

const Stepper = ({ status }) => {
  const step = ORDER_STATUS[status]?.step ?? 0;
  if (step < 0) return null;
  return (
    <div className="flex items-center gap-1" data-testid="order-stepper">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = step >= n;
        return (
          <div key={label} className="flex-1 min-w-0">
            <div className={`h-1.5 rounded-full ${done ? 'bg-emerald-500' : 'bg-gray-200'}`} />
            <div className={`text-[10px] mt-1 truncate ${done ? 'text-emerald-700 font-medium' : 'text-gray-400'}`}>{label}</div>
          </div>
        );
      })}
    </div>
  );
};

const Section = ({ title, children, right }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <div className="text-xs font-medium text-muted-foreground uppercase">{title}</div>
      {right}
    </div>
    {children}
  </div>
);

export const OrderDetailBody = ({ order, onChanged, onCancel, onRefund, onApproveReturn, onRejectReturn }) => {
  const [busy, setBusy] = useState('');
  const [bookOpen, setBookOpen] = useState(false);
  const s = order.status;

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      if (okMsg) toast.success(okMsg);
      onChanged?.(res?.data?.order);
      return res;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy('');
    }
  };
  const post = (path, body = {}) => api.post(`/ecom-admin/orders/${order.id}${path}`, body);
  const openDoc = (type) => act(`doc-${type}`, async () => {
    const res = await post('/documents', { type });
    window.open(res.data.url, '_blank', 'noopener');
    return res;
  });

  const canCancel = !['delivered', 'cancelled', 'returned', 'return_requested', 'return_approved', 'return_rejected',
    'picked_up', 'in_transit', 'shipped', 'out_for_delivery', 'rto'].includes(s);
  const canRefund = (order.refundable_amount ?? 0) > 0 && ['paid', 'cod_pending', 'partially_refunded'].includes(order.payment_status) && s !== 'pending_payment';
  const manualNext = {
    confirmed: ['packed'], packed: ['ready_to_ship', 'picked_up'], ready_to_ship: ['picked_up'], processing: ['picked_up'],
    picked_up: ['in_transit', 'out_for_delivery', 'delivered'], in_transit: ['out_for_delivery', 'delivered'],
    shipped: ['out_for_delivery', 'delivered'], out_for_delivery: ['delivered'],
  }[s] || [];

  return (
    <div className="space-y-5 mt-5" data-testid="order-detail">
      <Stepper status={s} />

      {/* Action bar */}
      <div className="flex flex-wrap gap-2" data-testid="order-actions">
        {s === 'paid' && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!!busy} data-testid="confirm-order-btn"
            onClick={() => act('confirm', () => post('/confirm'), 'Order confirmed · store stock deducted')}>
            {busy === 'confirm' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />} Confirm Order
          </Button>
        )}
        {s === 'confirmed' && (
          <Button size="sm" variant="outline" disabled={!!busy} data-testid="pack-order-btn"
            onClick={() => act('pack', () => post('/pack'), 'Marked as packed')}>
            <Package className="h-3.5 w-3.5 mr-1" /> Mark Packed
          </Button>
        )}
        {['confirmed', 'packed', 'processing'].includes(s) && !order.awb_code && (
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={!!busy} onClick={() => setBookOpen(true)} data-testid="book-shipment-btn">
            <Truck className="h-3.5 w-3.5 mr-1" /> Book Shipment
          </Button>
        )}
        {order.shiprocket_shipment_id && !['delivered', 'cancelled'].includes(s) && (
          <Button size="sm" variant="outline" disabled={!!busy} data-testid="sync-tracking-btn"
            onClick={() => act('sync', () => post('/sync-tracking'), 'Tracking updated from Shiprocket')}>
            {busy === 'sync' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Sync Tracking
          </Button>
        )}
        {order.shiprocket_shipment_id && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={!!busy} data-testid="documents-btn"><FileText className="h-3.5 w-3.5 mr-1" /> Documents <ChevronDown className="h-3 w-3 ml-1" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => openDoc('label')} data-testid="doc-label">Shipping Label</DropdownMenuItem>
              <DropdownMenuItem onClick={() => openDoc('manifest')} data-testid="doc-manifest">Manifest</DropdownMenuItem>
              <DropdownMenuItem onClick={() => openDoc('invoice')} data-testid="doc-invoice">Invoice</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {manualNext.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" disabled={!!busy} data-testid="manual-status-btn">Mark as… <ChevronDown className="h-3 w-3 ml-1" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {manualNext.map((st) => (
                <DropdownMenuItem key={st} data-testid={`mark-${st}`}
                  onClick={() => act(`mark-${st}`, () => post('/status', { status: st }), `Marked ${ORDER_STATUS[st]?.label}`)}>
                  {ORDER_STATUS[st]?.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {s === 'return_requested' && (
          <>
            <Button size="sm" onClick={onApproveReturn} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-return-btn"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve Return</Button>
            <Button size="sm" variant="outline" onClick={onRejectReturn} data-testid="reject-return-btn"><XCircle className="h-3.5 w-3.5 mr-1" /> Reject Return</Button>
          </>
        )}
        <div className="flex-1" />
        {canRefund && (
          <Button size="sm" variant="outline" onClick={onRefund} data-testid="refund-btn"><ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Refund</Button>
        )}
        {canCancel && (
          <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={onCancel} data-testid="cancel-btn"><XCircle className="h-3.5 w-3.5 mr-1" /> Cancel Order</Button>
        )}
      </div>

      {/* Shipment */}
      <Section title="Shipment" right={order.tracking_url && order.awb_code && (
        <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 flex items-center gap-1" data-testid="track-link">Track <ExternalLink className="h-3 w-3" /></a>
      )}>
        <Card data-testid="shipment-card"><CardContent className="p-4 text-sm space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><div className="text-xs text-muted-foreground">Courier</div><div className="font-medium">{order.courier_name || order.quoted_courier_name || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">AWB</div><div className="font-mono">{order.awb_code || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Pickup scheduled</div>
              <div className="font-medium" data-testid="pickup-date">{order.pickup_scheduled_date ? formatDate(order.pickup_scheduled_date) : '—'}</div>
              {order.pickup_token_number && <div className="text-[11px] text-muted-foreground">Token {order.pickup_token_number}</div>}
            </div>
            <div><div className="text-xs text-muted-foreground">Expected delivery</div>
              <div className="font-medium flex items-center gap-1" data-testid="eta-date"><Calendar className="h-3 w-3" />{order.expected_delivery_date ? formatDateOnly(order.expected_delivery_date) : order.estimated_delivery_days ? `~${order.estimated_delivery_days} days` : '—'}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Parcel {Number(order.shipment?.parcel_weight_kg ?? order.parcel_weight_kg ?? 0).toFixed(3)} kg{order.shipment?.has_estimated_weights ? ' (estimated)' : ''}</span>
            <span>Shipping charged {money(order.shipping_cost)}{order.shipping_note ? ` · ${order.shipping_note}` : ''}</span>
            {order.shiprocket_order_id && <span>SR order {order.shiprocket_order_id} · shipment {order.shiprocket_shipment_id}</span>}
            {order.shipment_status_text && <span>Courier status: <b className="text-foreground">{order.shipment_status_text}</b></span>}
            {order.last_tracked_at && <span>Synced {formatDate(order.last_tracked_at)}</span>}
          </div>
          {(order.label_url || order.manifest_url || order.invoice_url) && (
            <div className="flex gap-3 text-xs">
              {order.label_url && <a className="text-indigo-600 underline" href={order.label_url} target="_blank" rel="noreferrer">Label PDF</a>}
              {order.manifest_url && <a className="text-indigo-600 underline" href={order.manifest_url} target="_blank" rel="noreferrer">Manifest PDF</a>}
              {order.invoice_url && <a className="text-indigo-600 underline" href={order.invoice_url} target="_blank" rel="noreferrer">Invoice PDF</a>}
            </div>
          )}
          {order.tracking_events?.length > 0 && (
            <div className="border-t pt-2 max-h-40 overflow-y-auto space-y-1" data-testid="tracking-events">
              {order.tracking_events.map((ev, i) => (
                <div key={i} className="flex gap-3 text-xs"><span className="text-muted-foreground whitespace-nowrap w-32">{ev.date}</span><span className="font-medium">{ev.activity || ev.status}</span><span className="text-muted-foreground">{ev.location}</span></div>
              ))}
            </div>
          )}
          {IN_TRANSIT_SET.includes(s) && !order.shiprocket_shipment_id && (
            <div className="text-xs text-amber-700">Delivered by own logistics — update the stage with &ldquo;Mark as…&rdquo;.</div>
          )}
        </CardContent></Card>
      </Section>

      {/* Items */}
      <Section title={`Items (${order.items?.length || 0})`}>
        <div className="rounded-md border divide-y" data-testid="order-items">
          {order.items?.map((it, i) => (
            <div key={i} className="p-3 flex gap-3" data-testid={`order-item-${i}`}>
              <div className="h-14 w-14 rounded-md border bg-gray-50 overflow-hidden shrink-0 flex items-center justify-center">
                {it.image ? <img src={it.image} alt={it.name} className="h-full w-full object-cover" /> : <ImageOff className="h-5 w-5 text-gray-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{it.name}</div>
                <div className="text-xs text-muted-foreground">SKU {it.sku}{it.barcode ? ` · ${it.barcode}` : ''}{it.hsn_code ? ` · HSN ${it.hsn_code}` : ''} · Qty {it.qty}</div>
                <div className="text-xs mt-1 flex flex-wrap items-center gap-2">
                  <span>MRP {money(it.mrp)}</span>
                  {it.list_price && it.list_price !== it.selling_price && <span className="line-through text-muted-foreground">{money(it.list_price)}</span>}
                  <span className="font-medium">{money(it.selling_price)} / unit</span>
                  {it.offer_name && <Badge className="bg-pink-100 text-pink-800 border-0 text-[10px]"><Tag className="h-3 w-3 mr-1" />{it.offer_name}{it.offer_code ? ` (${it.offer_code})` : ''} · saved {money(it.offer_discount)}</Badge>}
                </div>
              </div>
              <div className="text-right font-semibold whitespace-nowrap">{money(it.line_total)}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Money */}
      <Section title="Payment">
        <Card><CardContent className="p-4 grid grid-cols-2 gap-y-1.5 text-sm" data-testid="order-money">
          <span className="text-muted-foreground">Subtotal (MRP)</span><span className="text-right">{money(order.subtotal_mrp)}</span>
          <span className="text-muted-foreground">Product discount</span><span className="text-right text-emerald-700">− {money(order.product_discount)}</span>
          {order.coupon_code && <><span className="text-muted-foreground">Coupon {order.coupon_code}</span><span className="text-right text-emerald-700">− {money(order.coupon_discount)}</span></>}
          {order.prepaid_discount > 0 && <><span className="text-muted-foreground">Prepaid discount</span><span className="text-right text-emerald-700">− {money(order.prepaid_discount)}</span></>}
          <span className="text-muted-foreground">Shipping</span><span className="text-right">{money(order.shipping_cost)}</span>
          <span className="font-semibold border-t pt-1.5">Grand total</span><span className="text-right font-semibold border-t pt-1.5">{money(order.grand_total)}</span>
          <span className="text-muted-foreground">Payment</span>
          <span className="text-right">{order.payment_method === 'cod' ? 'Cash on delivery' : 'Razorpay'} · <Badge variant="outline" className="capitalize">{(order.payment_status || '').replace('_', ' ')}</Badge></span>
          {order.razorpay_payment_id && <><span className="text-muted-foreground">Razorpay payment</span><span className="text-right font-mono text-xs">{order.razorpay_payment_id}</span></>}
          {order.refunded_amount > 0 && <><span className="text-muted-foreground">Refunded</span><span className="text-right text-rose-700">{money(order.refunded_amount)} · remaining {money(order.refundable_amount)}</span></>}
        </CardContent></Card>
        {order.refunds?.length > 0 && (
          <div className="mt-2 text-xs space-y-1" data-testid="refund-list">
            {order.refunds.map((r, i) => <div key={i} className="flex justify-between border rounded px-2 py-1"><span>{r.note}</span><span className="font-medium">{money(r.amount)} · {r.status}{r.razorpay_refund_id ? ` · ${r.razorpay_refund_id}` : ''}</span></div>)}
          </div>
        )}
      </Section>

      {/* Address */}
      <Section title="Delivery address">
        <Card><CardContent className="p-4 text-sm flex gap-2" data-testid="order-address">
          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div>
            <div className="font-medium">{order.delivery_address?.name}</div>
            <div>{order.delivery_address?.line1}{order.delivery_address?.line2 ? `, ${order.delivery_address.line2}` : ''}</div>
            <div>{order.delivery_address?.city}, {order.delivery_address?.state} — {order.delivery_address?.pincode}</div>
            <div className="text-muted-foreground">{order.delivery_address?.mobile}</div>
          </div>
        </CardContent></Card>
      </Section>

      {/* Timeline */}
      <Section title="Timeline">
        <div className="border rounded-md divide-y max-h-64 overflow-y-auto" data-testid="order-timeline">
          {[...(order.timeline || [])].reverse().map((t, i) => (
            <div key={i} className="px-3 py-2 text-sm flex justify-between gap-3">
              <div><span className="font-medium capitalize">{t.status.replace(/_/g, ' ')}</span>{t.note && <div className="text-xs text-muted-foreground">{t.note}</div>}</div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(t.at)}<div className="text-right">{t.source}</div></div>
            </div>
          ))}
        </div>
      </Section>

      <BookShipmentDialog order={order} open={bookOpen} onOpenChange={setBookOpen} onBooked={(o) => onChanged?.(o)} />
    </div>
  );
};

export { StatusBadge };
